import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import closeIcon from '../../Assets/Icons/close.svg';
import dropdownIcon from '../../Assets/Icons/dropdown.svg';

export default function UserTable({ onClose, title = "User List" }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [deliveryGroups, setDeliveryGroups] = useState([]);
    const [deliveryUnits, setDeliveryUnits] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedUnit, setSelectedUnit] = useState('');
    const [showDropdown, setShowDropdown] = useState({ group: false, unit: false });
    const closeButtonRef = useRef(null);
    const dropdownRefs = { group: useRef(null), unit: useRef(null) };

    // Optimized API Call
    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get('https://apihub-bk-prod-rd-f8a8hzcjgkdeezcd.southindia-01.azurewebsites.net/apirepo/v0.1/users');
            if (response.data.success) {
                const allUsers = response.data.data.Users;
                setUsers(allUsers);
                setLoading(false);
                setFilteredUsers(allUsers);

                setDeliveryGroups([...new Set(allUsers.map(user => user.DeliveryGroup).filter(Boolean))]);
                setDeliveryUnits([...new Set(allUsers.map(user => user.DeliveryUnit).filter(Boolean))]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Handle Dropdown Visibility
    useEffect(() => {
        if (closeButtonRef.current) closeButtonRef.current.focus();

        const handleClickOutside = (event) => {
            if (
                !dropdownRefs.group.current?.contains(event.target) &&
                !dropdownRefs.unit.current?.contains(event.target)
            ) {
                setShowDropdown({ group: false, unit: false });
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    });

    const handleFilterSelection = (type, value) => {
        let updatedGroup = selectedGroup;
        let updatedUnit = selectedUnit;

        // Update the selected group or unit
        if (type === 'group') updatedGroup = value;
        if (type === 'unit') updatedUnit = value;

        // Filter users based on the selected group and unit
        const filtered = users.filter(user =>
            (!updatedGroup || user.DeliveryGroup === updatedGroup) &&
            (!updatedUnit || user.DeliveryUnit === updatedUnit)
        );

        // Update the state with filtered users
        setFilteredUsers(filtered);

        // Close the dropdown after selection
        setShowDropdown(prev => ({ ...prev, [type]: false }));

        // Update the selected filter values
        setSelectedGroup(updatedGroup);
        setSelectedUnit(updatedUnit);
    };
    const handleDropdownDeliveryUnit = () => {
        console.log("Dropdown clicked")
        setShowDropdown({ ...showDropdown, unit: !showDropdown.unit })
    }
    useEffect(() => {
        console.log(showDropdown)
    }, [showDropdown])

    return (
        <div className="fixed inset-0 flex pt-5 justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded shadow-custom text-black w-[90vw] h-[90vh] max-w-6xl border border-gray-600 relative z-20">
                <div className="sticky top-0 bg-[#002746] text-white h-12 flex items-center justify-between px-4 shadow-custom z-10 mb-4">
                    <h1 className="font-semibold text-lg">{title}</h1>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="border-none hover:bg-slate-700 hover:rounded-full"
                        ref={closeButtonRef}
                    >
                        <img src={closeIcon} alt="close" className="w-8 h-8" />
                    </button>
                </div>

                {/* User Table */}
                <div className="px-4 max-h-[73vh] min-h-[50vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center mt-20 w-full">
                            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <table className="bg-white w-full border max-w-[85vw] border-gray-200">
                            <thead className="text-white border-b border-gray-200">
                                <tr className="bg-[#002746] border-b sticky top-[-1px] border-gray-200">
                                    <th className="text-left p-2 w-[90px] font-bold">Sr No</th>
                                    <th className="text-left p-2 font-bold">Name</th>
                                    <th className="text-left p-2 font-bold">Email</th>

                                    {/* Delivery Unit Dropdown */}
                                    <th className="text-left p-2 font-bold relative">
                                        <div className="flex items-center justify-between">
                                            <span>Delivery Unit</span>
                                            <img
                                                src={dropdownIcon}
                                                alt="dropdown"
                                                className="w-6 h-6 cursor-pointer"
                                                onClick={handleDropdownDeliveryUnit}
                                            />
                                        </div>
                                        {showDropdown.unit && (
                                            <div ref={dropdownRefs.unit} className="absolute mt-1 w-48 bg-white border rounded shadow-lg z-10">
                                                <div className="px-4 py-2 cursor-pointer text-black hover:bg-gray-100 font-semibold"
                                                    onClick={() => handleFilterSelection('unit', '')}>All</div>
                                                {deliveryUnits.map((unit, index) => (
                                                    <div key={index} className="px-4 py-2 text-black cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleFilterSelection('unit', unit)}>
                                                        {unit}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </th>

                                    {/* Delivery Group Dropdown */}
                                    <th className="text-left p-2 font-bold relative">
                                        <div className="flex items-center justify-between">
                                            <span>Delivery Group</span>
                                            <img
                                                src={dropdownIcon}
                                                alt="dropdown"
                                                className="w-6 h-6 cursor-pointer"
                                                onClick={() => setShowDropdown({ ...showDropdown, group: !showDropdown.group })}
                                            />
                                        </div>
                                        {showDropdown.group && (
                                            <div ref={dropdownRefs.group} className="absolute mt-1 w-48 bg-white border rounded shadow-lg z-10">
                                                <div className="px-4 py-2 cursor-pointer text-black hover:bg-gray-100 font-semibold"
                                                    onClick={() => handleFilterSelection('group', '')}>All</div>
                                                {deliveryGroups.map((group, index) => (
                                                    <div key={index} className="px-4 py-2 text-black cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleFilterSelection('group', group)}>
                                                        {group}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="bg-white text-black">
                                {filteredUsers.map((user, index) => (
                                    <tr key={user._id} className="border-b hover:bg-gray-100">
                                        <td className="p-2">{index + 1}</td>
                                        <td className="p-2">{user.firstName} {user.lastName}</td>
                                        <td className="p-2">{user.email}</td>
                                        <td className="p-2">{user.DeliveryUnit}</td>
                                        <td className="p-2">{user.DeliveryGroup}</td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>)}
                </div>
            </div>
        </div>
    );
}
